import { useState, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { AlertCircle, Wind, Activity, Layers, Waves, Heart, Youtube, Brain, Sparkles, Target, ClipboardList, ArrowRight, ChevronDown, Menu, X, Mail, MapPin } from 'lucide-react';

// =========================================================
// PREVIEW PAGE — Direction 1 "Souffle Organique"
// Bone white + sage palette, Cormorant Garamond + Figtree
// asymmetric organic feel, paper grain, fluid SVG breath lines.
// =========================================================

const C = {
  bg: '#F9F8F6',
  surface: '#F2EEE5',
  ink: '#2C423F',
  ink2: '#5A6B66',
  sage: '#8FA89B',
  sageDeep: '#6B8478',
  terre: '#E6DFD3',
  hairline: '#DDD4C5',
};

const HERO_IMG = 'https://static.prod-images.emergentagent.com/jobs/03019b9c-e38d-4235-95ae-da304b949bfe/images/0c2f710d7896e556371b0dd8501cd6f8572ec20a01199e7eefd3c317d50854b8.png';

const Serif = ({ children, className = '', as: As = 'span', italic = false, ...rest }) => (
  <As
    className={className}
    style={{
      fontFamily: '"Cormorant Garamond", Georgia, serif',
      letterSpacing: '-0.005em',
      fontStyle: italic ? 'italic' : 'normal',
    }}
    {...rest}
  >
    {children}
  </As>
);

const PillButton = ({ onClick, children, variant = 'primary' }) => {
  const base = 'inline-flex items-center gap-3 px-7 py-4 rounded-full text-sm font-medium transition-all duration-300 hover:gap-4';
  const styles = variant === 'primary'
    ? { backgroundColor: C.sage, color: C.bg, border: `1px solid ${C.sage}` }
    : { backgroundColor: 'transparent', color: C.ink, border: `1px solid ${C.ink}` };
  return (
    <button type="button" onClick={onClick} className={base} style={{ ...styles, fontFamily: 'Figtree, sans-serif' }}>
      <span>{children}</span>
      <ArrowRight size={16} />
    </button>
  );
};

const Eyebrow = ({ children }) => (
  <span
    className="inline-flex items-center gap-3 text-xs tracking-[0.25em] uppercase"
    style={{ color: C.sageDeep, fontFamily: 'Figtree, sans-serif' }}
  >
    <span className="w-8 h-px" style={{ backgroundColor: C.sageDeep }} />
    {children}
  </span>
);

// Subtle paper grain via CSS — applied as fixed background using SVG noise
const GrainOverlay = () => (
  <div
    aria-hidden
    className="pointer-events-none fixed inset-0 z-[1] opacity-[0.035] mix-blend-multiply"
    style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
    }}
  />
);

// Breath line: gentle wavy SVG separator
const BreathLine = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 1200 60" fill="none" preserveAspectRatio="none" aria-hidden>
    <path
      d="M0 30 Q 150 0, 300 30 T 600 30 T 900 30 T 1200 30"
      stroke={C.sage}
      strokeWidth="1"
      fill="none"
    />
  </svg>
);

const HomePagePreviewV1 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const closeTimeoutRef = useRef(null);

  const handleMouseEnter = (index) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setDropdownOpen(index);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setDropdownOpen(null);
    }, 200);
  };

  const menuItems = [
    { path: '/', label: 'Accueil', type: 'link' },
    {
      label: 'Troubles respiratoires', type: 'dropdown',
      items: [
        { path: '/hyperventilation', label: 'Hyperventilation' },
        { path: '/respiration-dysfonctionnelle', label: 'Respiration Dysfonctionnelle' },
        { path: '/asthme', label: 'Asthme' },
        { path: '/covid-long', label: 'COVID Long' },
      ],
    },
    {
      label: 'Ressources théoriques', type: 'dropdown',
      items: [
        { path: '/causes-troubles-respiratoires', label: 'Causes des Troubles Respiratoires' },
        { path: '/mecanique-respiratoire', label: 'Mécanique Respiratoire' },
        { path: '/respiration-optimale', label: 'Respiration Optimale' },
        { path: '/nerf-vague', label: 'Nerf Vague' },
      ],
    },
    {
      label: 'Techniques', type: 'dropdown',
      items: [
        { path: '/coherence-cardiaque', label: 'Cohérence Cardiaque' },
        { path: '/methode-buteyko', label: 'Méthode Buteyko' },
        { path: '/mobilite-mouvement', label: 'Mobilité et Mouvement' },
        { path: '/entrainement-muscles-respiratoires', label: 'Entraînement des Muscles Respiratoires' },
      ],
    },
    { path: '/evaluer-respiration', label: 'Tester sa respiration', type: 'link' },
    { path: '/documents', label: 'Documents', type: 'link' },
  ];

  const conditions = [
    { icon: AlertCircle, title: 'Hyperventilation', description: "Reconnaître et traiter le syndrome d'hyperventilation chronique.", link: '/hyperventilation' },
    { icon: Wind, title: 'Respiration Dysfonctionnelle', description: 'Comprendre les patterns respiratoires inadaptés et les corriger.', link: '/respiration-dysfonctionnelle' },
    { icon: Waves, title: 'Asthme', description: 'Techniques respiratoires pour gérer et réduire les symptômes.', link: '/asthme' },
    { icon: Activity, title: 'COVID Long', description: 'Rééducation respiratoire pour les symptômes persistants post-COVID.', link: '/covid-long' },
  ];

  const resources = [
    { icon: Brain, title: 'Causes des Troubles Respiratoires', description: 'Le rôle du système nerveux autonome.', link: '/causes-troubles-respiratoires' },
    { icon: Layers, title: 'Mécanique Respiratoire', description: 'Anatomie et physiologie de la respiration.', link: '/mecanique-respiratoire' },
    { icon: Sparkles, title: 'Respiration Optimale', description: 'Les six principes fondamentaux.', link: '/respiration-optimale' },
    { icon: Target, title: 'Méthode Buteyko', description: 'Améliorer la tolérance au CO₂.', link: '/methode-buteyko' },
    { icon: Heart, title: 'Cohérence Cardiaque', description: 'Harmoniser cœur, cerveau et émotions.', link: '/coherence-cardiaque' },
    { icon: ClipboardList, title: 'Évaluer Votre Respiration', description: 'BOLT, Nijmegen, fréquence respiratoire.', link: '/evaluer-respiration' },
  ];

  const benefits = [
    'Réduction des symptômes respiratoires',
    'Amélioration de la tolérance à l\'effort',
    'Diminution de l\'anxiété',
    'Meilleur sommeil',
    'Équilibre du système nerveux',
    'Augmentation de l\'énergie',
    'Réduction de l\'inflammation',
    'Amélioration de la concentration',
  ];

  return (
    <div
      className="min-h-screen relative"
      style={{ backgroundColor: C.bg, color: C.ink, fontFamily: 'Figtree, sans-serif' }}
      data-testid="homepage-v1"
    >
      <GrainOverlay />

      {/* ============ HEADER WITH DROPDOWNS ============ */}
      <header
        className="sticky top-0 z-40"
        style={{
          backgroundColor: 'rgba(249,248,246,0.55)',
          backdropFilter: 'saturate(180%) blur(24px)',
          WebkitBackdropFilter: 'saturate(180%) blur(24px)',
          borderBottom: `1px solid rgba(221,212,197,0.5)`,
          boxShadow: '0 1px 30px -10px rgba(44,66,63,0.06)',
        }}
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo: photo + name → profile modal */}
            <div className="flex items-center gap-3">
              <button onClick={() => setProfileModalOpen(true)} className="group relative">
                <img
                  src="/julien-jaccard.png"
                  alt="Julien Jaccard"
                  className="w-14 h-14 rounded-full object-cover transition-all group-hover:opacity-90"
                  style={{ border: `2px solid ${C.sage}` }}
                />
              </button>
              <button
                onClick={() => setProfileModalOpen(true)}
                className="hidden sm:block text-left"
                data-testid="open-profile-modal-name"
              >
                <Serif as="p" className="text-xl leading-tight transition-opacity group-hover:opacity-70" style={{ color: C.ink }}>
                  Julien Jaccard
                </Serif>
                <p className="text-xs mt-0.5" style={{ color: C.ink2 }}>Physiothérapeute</p>
              </button>
            </div>

            {/* Desktop nav with dropdowns */}
            <nav className="hidden lg:flex items-center gap-7">
              {menuItems.map((item, index) => (
                item.type === 'link' ? (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="text-sm transition-colors"
                    style={{
                      color: location.pathname === item.path ? C.sageDeep : C.ink,
                      fontFamily: 'Figtree, sans-serif',
                    }}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <div
                    key={item.label}
                    className="relative"
                    onMouseEnter={() => handleMouseEnter(index)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <button
                      className="flex items-center gap-1 text-sm transition-colors"
                      style={{ color: C.ink, fontFamily: 'Figtree, sans-serif' }}
                    >
                      {item.label}
                      <ChevronDown size={14} className={`transition-transform ${dropdownOpen === index ? 'rotate-180' : ''}`} />
                    </button>
                    {dropdownOpen === index && (
                      <div className="absolute top-full left-0 pt-3 w-72 z-50">
                        <div
                          className="rounded-2xl py-2 overflow-hidden"
                          style={{
                            backgroundColor: 'rgba(249,248,246,0.85)',
                            backdropFilter: 'saturate(180%) blur(20px)',
                            WebkitBackdropFilter: 'saturate(180%) blur(20px)',
                            border: `1px solid rgba(221,212,197,0.6)`,
                            boxShadow: '0 20px 50px -15px rgba(44,66,63,0.18)',
                          }}
                        >
                          {item.items.map((subItem) => (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              onClick={() => setDropdownOpen(null)}
                              className="block px-5 py-2.5 text-sm transition-colors"
                              style={{
                                color: location.pathname === subItem.path ? C.sageDeep : C.ink,
                                backgroundColor: location.pathname === subItem.path ? C.surface : 'transparent',
                                fontFamily: 'Figtree, sans-serif',
                              }}
                              onMouseEnter={(e) => { if (location.pathname !== subItem.path) e.currentTarget.style.backgroundColor = C.surface; }}
                              onMouseLeave={(e) => { if (location.pathname !== subItem.path) e.currentTarget.style.backgroundColor = 'transparent'; }}
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              ))}
              <PillButton onClick={() => navigate('/app-landing')}>Exercices</PillButton>
            </nav>

            {/* Mobile burger */}
            <button
              className="lg:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{ color: C.ink }}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile nav */}
          {mobileMenuOpen && (
            <nav className="lg:hidden py-4" style={{ borderTop: `1px solid ${C.hairline}` }}>
              <div className="flex flex-col gap-1">
                {menuItems.map((item, index) => (
                  item.type === 'link' ? (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className="px-4 py-2.5 rounded-lg text-sm transition-colors"
                      style={{
                        color: location.pathname === item.path ? C.sageDeep : C.ink,
                        backgroundColor: location.pathname === item.path ? C.surface : 'transparent',
                        fontFamily: 'Figtree, sans-serif',
                      }}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <div key={item.label} className="px-4">
                      <button
                        onClick={() => setDropdownOpen(dropdownOpen === index ? null : index)}
                        className="flex items-center justify-between w-full py-2.5 text-sm"
                        style={{ color: C.ink, fontFamily: 'Figtree, sans-serif' }}
                      >
                        {item.label}
                        <ChevronDown size={16} className={`transition-transform ${dropdownOpen === index ? 'rotate-180' : ''}`} />
                      </button>
                      {dropdownOpen === index && (
                        <div className="pl-4 mt-1 space-y-1 pb-2">
                          {item.items.map((subItem) => (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              onClick={() => setMobileMenuOpen(false)}
                              className="block px-3 py-2 rounded-lg text-sm"
                              style={{
                                color: location.pathname === subItem.path ? C.sageDeep : C.ink2,
                                fontFamily: 'Figtree, sans-serif',
                              }}
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                ))}
                <div className="px-4 mt-3">
                  <PillButton onClick={() => { setMobileMenuOpen(false); navigate('/app-landing'); }}>
                    Accéder aux exercices
                  </PillButton>
                </div>
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* ============ PROFILE MODAL ============ */}
      {profileModalOpen && (
        <div
          className="fixed inset-0 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ zIndex: 9999, backgroundColor: 'rgba(44,66,63,0.55)' }}
          onClick={() => setProfileModalOpen(false)}
        >
          <div
            className="rounded-[28px] max-w-md w-full p-10 relative animate-in zoom-in-95 duration-200"
            style={{ backgroundColor: C.bg, border: `1px solid ${C.hairline}`, boxShadow: '0 40px 80px -30px rgba(44,66,63,0.4)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setProfileModalOpen(false)}
              className="absolute top-4 right-4 p-2 rounded-full transition-colors"
              style={{ color: C.ink2 }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = C.surface; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <X size={20} />
            </button>
            <div className="flex flex-col items-center text-center">
              <img
                src="/julien-jaccard.png"
                alt="Julien Jaccard"
                className="w-44 h-44 rounded-full object-cover mb-6"
                style={{ border: `3px solid ${C.sage}` }}
              />
              <Serif as="h2" className="text-3xl mb-2" style={{ color: C.ink }}>Julien Jaccard</Serif>
              <p className="text-base mb-1" style={{ color: C.ink2 }}>Physiothérapeute</p>
              <div className="flex items-center gap-2 mb-7" style={{ color: C.ink2 }}>
                <MapPin size={16} />
                <span className="text-sm">Valais, Suisse</span>
              </div>
              <a
                href="mailto:julienjaccard@mains-nuages.ch"
                className="inline-flex items-center gap-3 px-7 py-4 rounded-full text-sm font-medium transition-all"
                style={{ backgroundColor: C.sage, color: C.bg, fontFamily: 'Figtree, sans-serif' }}
              >
                <Mail size={16} />
                julienjaccard@mains-nuages.ch
              </a>
            </div>
          </div>
        </div>
      )}

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden">
        <div className="max-w-[1320px] mx-auto px-8 pt-20 pb-32 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Image first on mobile rendering order below text on desktop. We keep image col-span-5 right */}
            <div className="lg:col-span-7 lg:pr-8">
              <Eyebrow>Rééducation respiratoire</Eyebrow>
              <Serif as="h1" className="mt-8 text-[clamp(2.75rem,6vw,5.5rem)] leading-[0.98] font-medium">
                Retrouvez<br />
                <Serif italic style={{ color: C.sage }}>votre souffle</Serif>{' '}
                <Serif italic style={{ color: C.sage }}>vital.</Serif>
              </Serif>
              <p className="mt-10 text-lg leading-relaxed max-w-xl" style={{ color: C.ink2 }}>
                Une approche douce et scientifiquement fondée pour soulager hyperventilation, respiration dysfonctionnelle, asthme et COVID long. Un souffle juste, un système nerveux apaisé.
              </p>
              <div className="mt-12 flex flex-wrap items-center gap-4">
                <PillButton onClick={() => navigate('/app-landing')}>Commencer les exercices</PillButton>
                <PillButton onClick={() => navigate('/evaluer-respiration')} variant="ghost">Tester sa respiration</PillButton>
                <button
                  type="button"
                  onClick={() => navigate('/youtube')}
                  className="inline-flex items-center gap-3 px-7 py-4 rounded-full text-sm font-medium transition-all duration-300 hover:gap-4"
                  style={{
                    backgroundColor: '#C9483B',
                    color: C.bg,
                    border: '1px solid #C9483B',
                    fontFamily: 'Figtree, sans-serif',
                  }}
                >
                  <Youtube size={16} />
                  <span>Ma chaîne YouTube</span>
                </button>
              </div>

              {/* Decorative breath line */}
              <BreathLine className="mt-20 w-full h-10 opacity-50" />
            </div>

            {/* Hero image */}
            <div className="lg:col-span-5 relative">
              <div
                className="relative aspect-[3/4] w-full overflow-hidden rounded-[40px]"
                style={{
                  boxShadow: '0 30px 70px -25px rgba(44,66,63,0.25)',
                }}
              >
                <img src={HERO_IMG} alt="Silhouette respirant dans la lumière du matin" className="w-full h-full object-cover" />
              </div>

              {/* Floating badge */}
              <div
                className="absolute -bottom-6 -left-6 px-6 py-5 rounded-3xl"
                style={{
                  backgroundColor: C.bg,
                  border: `1px solid ${C.hairline}`,
                  boxShadow: '0 10px 30px -15px rgba(44,66,63,0.15)',
                }}
              >
                <Eyebrow>Depuis 1999</Eyebrow>
                <Serif as="p" className="text-3xl mt-2">25 ans d&apos;expérience</Serif>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative sage blob top right */}
        <div
          aria-hidden
          className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full opacity-40"
          style={{
            background: `radial-gradient(circle at 30% 30%, ${C.sage}40, transparent 70%)`,
          }}
        />
      </section>

      {/* ============ ABOUT ============ */}
      <section className="relative py-32" style={{ backgroundColor: C.surface }}>
        <div className="max-w-[1100px] mx-auto px-8">
          <div className="grid md:grid-cols-12 gap-12">
            <div className="md:col-span-4">
              <Eyebrow>L&apos;approche</Eyebrow>
              <Serif as="h2" className="mt-6 text-4xl md:text-5xl leading-tight">
                Comprendre<br />avant de soigner.
              </Serif>
            </div>
            <div className="md:col-span-7 md:col-start-6 space-y-6">
              <p className="text-base md:text-lg leading-relaxed" style={{ color: C.ink }}>
                Je suis <strong>Physiothérapeute depuis 1999</strong>, je me suis passionné pour les troubles respiratoires depuis 5 ans et ai étudié, pratiqué sur moi-même toutes les techniques respiratoires, et développé une compréhension dans mon corps du fonctionnement de cette machine organique que j&apos;appelle <em>«&nbsp;Moi&nbsp;»</em> qui me permet de <strong>VOUS comprendre</strong>, d&apos;identifier tous les paramètres de chacune de nos vies, chacune totalement unique, qui influencent le fonctionnement de cette machine humaine, en perturbent son équilibre, et la forcent à s&apos;adapter, continuellement, pour un seul but&nbsp;: maintenir la vie, à tout prix.
              </p>
              <p className="text-base md:text-lg leading-relaxed" style={{ color: C.ink }}>
                Les symptômes que nous ressentons, les inconforts, les douleurs, reflètent ces adaptations. Ce ne sont pas des <em>«&nbsp;problèmes&nbsp;»</em>, mais des <strong>solutions</strong> que le corps trouve pour s&apos;adapter aux multiples stresseurs qui tentent de le déséquilibrer. Par la compréhension de ce fonctionnement, et l&apos;analyse de notre vie propre, on peut retrouver l&apos;équilibre, ramener le corps dans un espace de <em>«&nbsp;sécurité&nbsp;»</em>, et retirer au corps le besoin de créer ces adaptations. <strong>La respiration est une des portes d&apos;entrées</strong> dont nous disposons pour retrouver cet équilibre, et le bien-être qui en découle.
              </p>
            </div>
          </div>
        </div>
      </section>

      <BreathLine className="w-full h-12 opacity-30" />

      {/* ============ BENEFITS ============ */}
      <section className="py-32 relative">
        <div className="max-w-[1100px] mx-auto px-8">
          <div className="grid md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-5">
              <Eyebrow>Bénéfices</Eyebrow>
              <Serif as="h2" className="mt-6 text-4xl md:text-5xl leading-tight">
                Ce que <Serif italic style={{ color: C.sage }}>la respiration</Serif> transforme.
              </Serif>
              <p className="mt-8 text-base leading-relaxed" style={{ color: C.ink2 }}>
                Vingt mille respirations par jour. Une fonction vitale dont la justesse rayonne sur l&apos;ensemble du corps et de l&apos;esprit.
              </p>
            </div>
            <ul className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
              {benefits.map((b, i) => (
                <li
                  key={b}
                  className="py-5 flex items-baseline gap-4"
                  style={{ borderBottom: `1px solid ${C.hairline}` }}
                >
                  <span className="text-xs tabular-nums tracking-[0.2em]" style={{ color: C.sageDeep }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="text-base leading-snug">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ============ CONDITIONS ============ */}
      <section className="py-32">
        <div className="max-w-[1320px] mx-auto px-8">
          <div className="mb-20 max-w-2xl">
            <Eyebrow>Troubles traités</Eyebrow>
            <Serif as="h2" className="mt-6 text-4xl md:text-5xl leading-tight">
              Quatre <Serif italic style={{ color: C.sage }}>familles</Serif> de dysfonctions respiratoires.
            </Serif>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {conditions.map((c) => (
              <button
                key={c.title}
                onClick={() => navigate(c.link)}
                className="text-left p-10 rounded-[28px] transition-all duration-300 hover:-translate-y-1 group"
                style={{
                  backgroundColor: C.surface,
                  border: `1px solid ${C.hairline}`,
                }}
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mb-8"
                  style={{ backgroundColor: C.bg, border: `1px solid ${C.hairline}` }}
                >
                  <c.icon size={20} strokeWidth={1.5} style={{ color: C.sageDeep }} />
                </div>
                <Serif as="h3" className="text-3xl mb-3 leading-snug">{c.title}</Serif>
                <p className="text-sm leading-relaxed mb-6" style={{ color: C.ink2 }}>{c.description}</p>
                <span
                  className="inline-flex items-center gap-2 text-sm font-medium transition-transform duration-300 group-hover:gap-3"
                  style={{ color: C.sageDeep }}
                >
                  Découvrir <ArrowRight size={14} />
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ============ RESOURCES ============ */}
      <section className="py-32" style={{ backgroundColor: C.surface }}>
        <div className="max-w-[1320px] mx-auto px-8">
          <div className="grid md:grid-cols-12 gap-12 mb-16">
            <div className="md:col-span-6">
              <Eyebrow>Comprendre</Eyebrow>
              <Serif as="h2" className="mt-6 text-4xl md:text-5xl leading-tight">
                Ressources<br />
                <Serif italic style={{ color: C.sage }}>éducatives.</Serif>
              </Serif>
            </div>
            <p className="md:col-span-5 md:col-start-8 text-base leading-relaxed self-end" style={{ color: C.ink2 }}>
              Anatomie, physiologie, techniques, outils d&apos;auto-évaluation. Six portes pour mieux comprendre votre souffle.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {resources.map((r) => (
              <button
                key={r.title}
                onClick={() => navigate(r.link)}
                className="text-left p-8 rounded-[24px] transition-all duration-300 hover:-translate-y-1 group"
                style={{
                  backgroundColor: C.bg,
                  border: `1px solid ${C.hairline}`,
                }}
              >
                <r.icon size={22} strokeWidth={1.5} style={{ color: C.sageDeep }} className="mb-6" />
                <Serif as="h3" className="text-2xl leading-snug mb-2">{r.title}</Serif>
                <p className="text-sm leading-relaxed" style={{ color: C.ink2 }}>{r.description}</p>
                <span
                  className="inline-flex items-center gap-2 mt-5 text-sm font-medium transition-transform duration-300 group-hover:gap-3"
                  style={{ color: C.sageDeep }}
                >
                  Lire <ArrowRight size={14} />
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ============ AVERTISSEMENT ============ */}
      <section className="pb-24">
        <div className="max-w-[900px] mx-auto px-8">
          <div
            className="rounded-[28px] p-10"
            style={{ backgroundColor: C.surface, border: `1px solid ${C.hairline}` }}
          >
            <Eyebrow>Avertissement</Eyebrow>
            <p className="mt-5 text-base leading-relaxed" style={{ color: C.ink2 }}>
              Les informations et exercices décrits ici sont à titre informatif. Les problématiques évoquées relèvent d&apos;un diagnostic médical et d&apos;un accompagnement par un professionnel de santé spécialisé. Ces pages résument succinctement le contenu de séances thérapeutiques individualisées.{' '}
              <Link to="/contact" className="underline" style={{ color: C.sageDeep }}>Me contacter</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="relative py-40 overflow-hidden" style={{ backgroundColor: C.ink, color: C.bg }}>
        <div
          aria-hidden
          className="absolute inset-0 opacity-30"
          style={{
            background: `radial-gradient(circle at 70% 50%, ${C.sage}, transparent 60%)`,
          }}
        />
        <div className="max-w-[1100px] mx-auto px-8 text-center relative">
          <Eyebrow>
            <span style={{ color: C.sage }}>Commencer</span>
          </Eyebrow>
          <Serif as="h2" className="mt-8 text-5xl md:text-7xl leading-[1.02]">
            Prêt à <Serif italic style={{ color: C.sage }}>respirer</Serif><br />librement&nbsp;?
          </Serif>
          <p className="mt-10 text-lg max-w-xl mx-auto leading-relaxed" style={{ color: 'rgba(249,248,246,0.7)' }}>
            Accédez gratuitement aux exercices guidés et commencez votre parcours respiratoire dès aujourd&apos;hui.
          </p>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
            <button
              onClick={() => navigate('/app-landing')}
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full text-sm font-medium transition-all duration-300 hover:gap-4"
              style={{
                backgroundColor: C.sage,
                color: C.ink,
                fontFamily: 'Figtree, sans-serif',
              }}
            >
              Commencer maintenant
              <ArrowRight size={16} />
            </button>
            <button
              onClick={() => navigate('/youtube')}
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full text-sm font-medium transition-all"
              style={{
                color: C.bg,
                border: '1px solid rgba(249,248,246,0.3)',
                fontFamily: 'Figtree, sans-serif',
              }}
            >
              <Youtube size={16} />
              Chaîne YouTube
            </button>
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="py-14" style={{ backgroundColor: C.bg }}>
        <div className="max-w-[1320px] mx-auto px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <Serif as="div" className="text-2xl">Julien Jaccard</Serif>
            <p className="text-sm mt-1" style={{ color: C.ink2 }}>Physiothérapie · Rééducation respiratoire</p>
          </div>
          <div className="flex flex-col md:flex-row gap-x-10 gap-y-2 text-sm" style={{ color: C.ink2 }}>
            <a href="mailto:julienjaccard@mains-nuages.ch" style={{ color: C.sageDeep }} className="underline-offset-4 hover:underline">julienjaccard@mains-nuages.ch</a>
            <Link to="/contact" className="hover:opacity-70">Contact</Link>
            <Link to="/documents" className="hover:opacity-70">Documents</Link>
          </div>
          <p className="text-xs uppercase tracking-[0.25em]" style={{ color: C.sageDeep }}>
            © {new Date().getFullYear()} · Mains Nuages
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HomePagePreviewV1;
